import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, CreditCard, Banknote, Building, Receipt, DollarSign, Wallet, CircleDollarSign, Check, X } from 'lucide-react';
import api from '../api/axios';

const ICON_MAP = {
  CreditCard: CreditCard,
  Banknote: Banknote,
  Building: Building,
  Receipt: Receipt,
  DollarSign: DollarSign,
  Wallet: Wallet,
  Coins: CircleDollarSign,
  BadgeEuro: DollarSign,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

const PaymentMethodsConfig = () => {
  const navigate = useNavigate();
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editMethod, setEditMethod] = useState(null);
  const [form, setForm] = useState({ method_id: '', label: '', icon: 'CreditCard', is_cash: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchMethods();
  }, [navigate]);

  const fetchMethods = async () => {
    try {
      const res = await api.get('/payment-methods');
      setMethods(res.data);
    } catch {
      toast({ title: 'Erreur chargement', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const openAdd = () => {
    setForm({ method_id: '', label: '', icon: 'CreditCard', is_cash: false });
    setEditMethod(null);
    setShowAdd(true);
  };

  const openEdit = (m) => {
    setForm({ method_id: m.method_id, label: m.label, icon: m.icon || 'CreditCard', is_cash: m.is_cash || false });
    setEditMethod(m);
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) {
      toast({ title: 'Le nom est requis', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editMethod) {
        await api.put(`/payment-methods/${editMethod.method_id}`, {
          label: form.label,
          icon: form.icon,
          is_cash: form.is_cash,
        });
        toast({ title: 'Mode de paiement modifie' });
      } else {
        const methodId = form.method_id.trim() || form.label.trim();
        await api.post('/payment-methods', {
          method_id: methodId,
          label: form.label.trim(),
          icon: form.icon,
          is_cash: form.is_cash,
        });
        toast({ title: 'Mode de paiement cree' });
      }
      setShowAdd(false);
      await fetchMethods();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleToggle = async (m) => {
    try {
      await api.put(`/payment-methods/${m.method_id}`, { active: !m.active });
      toast({ title: m.active ? 'Mode desactive' : 'Mode reactive' });
      await fetchMethods();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Desactiver le mode "${m.label}" ?`)) return;
    try {
      await api.delete(`/payment-methods/${m.method_id}`);
      toast({ title: 'Mode de paiement desactive' });
      await fetchMethods();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleReorder = async (idx, direction) => {
    const newMethods = [...methods];
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= newMethods.length) return;
    [newMethods[idx], newMethods[swapIdx]] = [newMethods[swapIdx], newMethods[idx]];
    setMethods(newMethods);
    try {
      await Promise.all(newMethods.map((m, i) =>
        api.put(`/payment-methods/${m.method_id}`, { order: i + 1 })
      ));
    } catch {}
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-3 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate('/staff/dashboard')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Modes de Paiement</h1>
            <p className="text-xs text-gray-300">Configurer les moyens de paiement acceptes</p>
          </div>
          <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-method-btn">
            <Plus size={16} className="mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card className="border-2 border-[#d4c4b0] mb-4">
          <CardHeader className="bg-[#f5f0ea] pb-2">
            <CardTitle className="text-sm text-gray-700">Modes de paiement configures</CardTitle>
            <p className="text-[10px] text-gray-500">Ces modes apparaissent dans l'ecran d'encaissement. Le mode "Mixte" est automatique et permet de combiner plusieurs modes.</p>
          </CardHeader>
          <CardContent className="p-0">
            {methods.length === 0 ? (
              <p className="text-center py-8 text-gray-400">Aucun mode de paiement configure</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {methods.map((m, idx) => {
                  const IconComp = ICON_MAP[m.icon] || CreditCard;
                  return (
                    <div key={m.method_id} className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${!m.active ? 'opacity-40' : ''}`} data-testid={`method-row-${m.method_id}`}>
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => handleReorder(idx, -1)} className="text-gray-300 hover:text-gray-600 text-xs" disabled={idx === 0}>▲</button>
                        <button onClick={() => handleReorder(idx, 1)} className="text-gray-300 hover:text-gray-600 text-xs" disabled={idx === methods.length - 1}>▼</button>
                      </div>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${m.active ? 'bg-[#3d2817] text-white' : 'bg-gray-200 text-gray-400'}`}>
                        <IconComp size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm">{m.label}</span>
                          <span className="text-[10px] text-gray-400 font-mono">({m.method_id})</span>
                          {m.is_cash && <Badge className="bg-green-100 text-green-700 border-green-200 text-[8px] h-4">Especes</Badge>}
                          {!m.active && <Badge className="bg-red-100 text-red-700 border-red-200 text-[8px] h-4">Desactive</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => handleToggle(m)} className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all ${m.active ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'}`} data-testid={`toggle-${m.method_id}`}>
                          {m.active ? <><Check size={10} className="inline mr-0.5" />Actif</> : <><X size={10} className="inline mr-0.5" />Inactif</>}
                        </button>
                        <Button onClick={() => openEdit(m)} variant="outline" size="sm" className="h-7 text-xs px-2" data-testid={`edit-${m.method_id}`}>
                          <Pencil size={12} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="py-3 px-4">
            <div className="flex items-start gap-2">
              <DollarSign size={16} className="text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-800">Mode "Mixte" automatique</p>
                <p className="text-[10px] text-amber-700 mt-0.5">Le mode "Mixte" est toujours disponible et permet de ventiler un paiement entre plusieurs modes actifs. Pas besoin de le configurer.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-sm" data-testid="method-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">{editMethod ? 'Modifier le mode de paiement' : 'Nouveau mode de paiement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {!editMethod && (
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Identifiant technique</label>
                <Input value={form.method_id} onChange={e => setForm({ ...form, method_id: e.target.value })}
                  placeholder="Ex: CB, Espece, Ticket..." className="h-9" data-testid="method-id-input" />
                <p className="text-[10px] text-gray-400 mt-0.5">Utilise en interne. Si vide, le nom sera utilise.</p>
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Nom affiche</label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="Ex: Carte Bancaire, Especes..." className="h-9" data-testid="method-label-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Icone</label>
              <div className="flex gap-1.5 flex-wrap">
                {ICON_OPTIONS.map(iconKey => {
                  const Ic = ICON_MAP[iconKey];
                  return (
                    <button key={iconKey} onClick={() => setForm({ ...form, icon: iconKey })}
                      className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${form.icon === iconKey ? 'bg-[#3d2817] text-white border-[#3d2817]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                      data-testid={`icon-${iconKey}`}>
                      <Ic size={16} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_cash} onChange={e => setForm({ ...form, is_cash: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300" data-testid="is-cash-checkbox" />
                <span className="text-xs font-semibold text-gray-700">Paiement en especes (calcul de la monnaie)</span>
              </label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white" data-testid="save-method-btn">
              {saving ? 'Enregistrement...' : editMethod ? 'Enregistrer' : 'Creer le mode de paiement'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodsConfig;
